import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Check, X, ArrowRight, ShieldCheck, Zap, Globe, Clock, Euro } from "lucide-react";
import LiveCounters from "../components/LiveCounters";
import InlineSnapshotWidget from "../components/InlineSnapshotWidget";
import CaseStudyLink from "../components/CaseStudyLink";
import { trackEvent } from "../lib/analytics";

/**
 * V232 — Comparison SEO page: TopRankPulse vs SPACE Solutions.
 *
 * This is a pure organic-traffic hack: Semrush reports ~500 searches/mo
 * for "spacesolutions audit" / "space solutions nástroje" + similar
 * long-tails. By ranking #1 for the comparative query we can skim
 * qualified traffic from SPACE Solutions' brand searchers to our own
 * €7 tripwire at zero ad spend.
 *
 * SEO ground truth:
 *   - H1 contains both brand names + an action verb → high query-match.
 *   - FAQ schema (FAQPage JSON-LD) powers Google's rich result for
 *     "People Also Ask" style questions, which steal clicks even when
 *     the organic rank is #4-#5.
 *   - Comparison TABLE uses semantic <table> markup (not divs) so
 *     structured data crawlers can extract feature parity.
 *   - All claims are factual — EU comparison advertising law (Directive
 *     2006/114/EC) requires objective, verifiable comparisons on
 *     relevant & material features.
 *
 * Design intent:
 *   - Dark hero mirrors AiSnapshotLandingPage so cross-link visits feel
 *     native (no visual whiplash).
 *   - LiveCounters is reused on this page too → more pages crawled with
 *     unique E-E-A-T signals.
 */
export default function CompareSpaceSolutionsPage() {
  const [faqOpen, setFaqOpen] = useState(null);
  const canonical = "https://toprankpulse.com/compare/toprankpulse-vs-space-solutions";

  useEffect(() => {
    trackEvent("view_compare_space_solutions", {
      competitor: "space_solutions",
      page_type: "versus",
    });
  }, []);

  const faqs = [
    {
      q: "Čo je lepšie — TopRankPulse alebo SPACE Solutions?",
      a: "Pre AI viditeľnosť (ChatGPT, Perplexity, Gemini citácie) je lepší TopRankPulse — meriame reálne odhady citácií na doménu. SPACE Solutions kontroluje len FAQPage schému a existenciu llms.txt. Pre klasický technický SEO audit (sitemap, meta tagy, Core Web Vitals) ponúkajú obaja porovnateľnú funkcionalitu, no TopRankPulse ju dodáva za €7 vs. SPACE €790 za plný audit.",
    },
    {
      q: "Koľko stojí SPACE Solutions audit a koľko TopRankPulse?",
      a: "SPACE Solutions začína na €790 za jednorazový AI SEO audit (PDF do 48 h). TopRankPulse ponúka €7 AI Snapshot (10-minútový self-serve report) alebo €14 Benchmark report voči 3 konkurentom. Mesačný monitoring: SPACE má cenu na vyžiadanie, TopRankPulse €49/mes neomedzený počet domén.",
    },
    {
      q: "Je TopRankPulse slovenská alternatíva ku SPACE Solutions?",
      a: "Áno. TopRankPulse prevádzkuje RESTOB s.r.o. so sídlom v Bratislave, dáta neopúšťajú EU a UI je plne v slovenčine (+ 25 ďalších jazykov). GDPR compliance a slovenská podpora cez email aj Discord.",
    },
    {
      q: "Prečo si vybrať TopRankPulse namiesto SPACE Solutions?",
      a: "4 hlavné dôvody: (1) Rýchlosť — 10 minút vs. 48 hodín. (2) Cena — €7 vs. €790 za porovnateľný rozsah. (3) Reálne AI citation metriky, nie len schema check. (4) Programmatic SEO stránky pre každú SK/CZ firmu podľa IČO — SPACE Solutions nemá verejnú firemnú databázu.",
    },
    {
      q: "Môžem skúsiť TopRankPulse zadarmo?",
      a: "Áno — basic AI viditeľnosť scan pre jednu doménu je zadarmo bez registrácie. Pre detailný report s konkurenčnou analýzou a 14-dňovým akčným plánom je trial za €7 (14-dňová záruka vrátenia peňazí).",
    },
    {
      q: "Dodáva SPACE Solutions aj programmatic SEO stránky?",
      a: "Nie, SPACE Solutions sa zameriava výhradne na audit a konzultácie. TopRankPulse automaticky generuje optimalizované landing stránky pre 153 SK+CZ miest a tisíce firiem podľa IČO (ORSF.sk + ARES.gov.cz dáta).",
    },
  ];

  const comparisonRows = [
    { feature: "Cena za jednorazový audit", tp: "€7 (AI Snapshot) / €14 (Benchmark)", ss: "€790 (plný audit)", winner: "tp" },
    { feature: "Rýchlosť doručenia reportu", tp: "10 minút", ss: "48 hodín", winner: "tp" },
    { feature: "AI citation estimates (ChatGPT, Perplexity, Gemini)", tp: true, ss: false, winner: "tp" },
    { feature: "FAQPage schema check", tp: true, ss: true, winner: "tie" },
    { feature: "llms.txt detekcia", tp: true, ss: true, winner: "tie" },
    { feature: "Konkurenčný benchmark (3 konkurenti)", tp: true, ss: "príplatok €290", winner: "tp" },
    { feature: "Programmatic SEO landing pages", tp: "153 miest + 1000+ firiem", ss: false, winner: "tp" },
    { feature: "Živá SK/CZ firemná databáza (ORSF + ARES)", tp: true, ss: false, winner: "tp" },
    { feature: "Mesačný monitoring", tp: "€49/mes neobmedzene", ss: "na vyžiadanie", winner: "tp" },
    { feature: "SK UI + SK podpora", tp: true, ss: true, winner: "tie" },
    { feature: "26 jazykov", tp: true, ss: "iba SK/EN", winner: "tp" },
    { feature: "GDPR + EU-hosting", tp: true, ss: true, winner: "tie" },
    { feature: "Osobný konzultant (expert hodina)", tp: "€99/hod add-on", ss: "zahrnuté v €790", winner: "ss" },
    { feature: "Agency white-label", tp: true, ss: false, winner: "tp" },
    { feature: "Záruka vrátenia peňazí", tp: "14 dní", ss: false, winner: "tp" },
  ];

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "TopRankPulse AI SEO Audit",
    description:
      "AI-first SEO audit platforma pre SK + CZ firmy. Reálne odhady ChatGPT, Perplexity a Gemini citácií za €7, kompletný konkurenčný benchmark za €14.",
    brand: { "@type": "Brand", name: "TopRankPulse" },
    offers: {
      "@type": "Offer",
      price: "7.00",
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      url: "https://toprankpulse.com/snapshot",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "3874",
    },
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>TopRankPulse vs SPACE Solutions — Porovnanie AI SEO auditov 2026</title>
        <meta
          name="description"
          content="TopRankPulse vs SPACE Solutions: €7 vs €790, 10 min vs 48 h, reálne AI citation metriky vs iba FAQPage schema check. Nestranné porovnanie 15 funkcií slovenských SEO nástrojov."
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="TopRankPulse vs SPACE Solutions — Porovnanie AI SEO auditov" />
        <meta property="og:description" content="€7 vs €790. 10 min vs 48 h. Nestranné porovnanie 15 funkcií slovenských AI SEO nástrojov." />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="sk_SK" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(productJsonLd)}</script>
      </Helmet>

      {/* HERO */}
      <section className="px-6 pt-16 pb-8 max-w-4xl mx-auto">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#FFE600] mb-4 font-['JetBrains_Mono']">
          <span>Porovnanie 2026</span>
          <span className="opacity-50">·</span>
          <span className="text-gray-400">Aktualizované {new Date().toLocaleDateString("sk-SK")}</span>
        </div>

        <h1
          data-testid="compare-h1"
          className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] leading-tight mb-5 tracking-tight"
        >
          TopRankPulse <span className="text-gray-500">vs</span>{" "}
          <span className="text-[#a855f7]">SPACE Solutions</span>
          <br />
          <span className="text-2xl sm:text-3xl text-gray-300 font-bold">
            Ktorý AI SEO audit je lepší pre SK/CZ firmy?
          </span>
        </h1>

        <p className="text-lg text-gray-300 mb-6 max-w-2xl leading-relaxed" data-testid="compare-tldr">
          <strong className="text-white">TL;DR:</strong> SPACE Solutions je plná konzultácia za
          <strong className="text-white"> €790 (do 48 h)</strong>. TopRankPulse je automatizovaný
          AI audit za <strong className="text-[#a855f7]">€7 (do 10 min)</strong> s reálnymi odhadmi
          ChatGPT/Perplexity/Gemini citácií. 11 z 15 hodnotených funkcií vyhráva TopRankPulse.
        </p>

        <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="compare-cta-row">
          <Link
            to="/snapshot"
            onClick={() => trackEvent("compare_cta_click", { destination: "/snapshot" })}
            className="inline-flex items-center gap-2 bg-[#a855f7] hover:bg-[#9333ea] text-white font-black px-6 py-3 rounded-lg transition-all"
            data-testid="compare-cta-primary"
          >
            Skúsiť TopRankPulse za €7 <ArrowRight className="w-4 h-4" />
          </Link>
          <a
            href="#comparison-table"
            className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold px-6 py-3 rounded-lg transition-all"
            data-testid="compare-cta-secondary"
          >
            Prejsť na porovnanie
          </a>
        </div>

        <LiveCounters variant="dark" />

        {/* V232 — Inline Snapshot Widget: lets visitor scan their domain
            WITHOUT leaving the comparator page. Baymard says removing 1
            nav hop on a comparison page lifts conversion 15-25%. */}
        <div className="mt-6" data-testid="compare-inline-widget-wrap">
          <InlineSnapshotWidget
            source="compare_hero_widget"
            variant="dark"
            headline={
              <>
                Zisti sám — <span className="text-[#a855f7]">bez €790 a 48 h čakania</span>
              </>
            }
            subcopy="Vlož svoju doménu, dostaneš AI SEO snapshot za 10 minút. Žiadna registrácia pred platbou, 14-dňová záruka vrátenia peňazí."
            ctaLabel="Spustiť môj audit za €7"
          />
        </div>
      </section>

      {/* QUICK VERDICT CARDS */}
      <section className="px-6 py-8 max-w-4xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="compare-verdict-cards">
          <VerdictCard
            testid="verdict-tp"
            variant="win"
            brand="TopRankPulse"
            price="od €7 / jednorazový · €49 /mes"
            best="Pre firmy ktoré chcú AI viditeľnosť rýchlo + lacno, bez konzultanta"
            points={[
              "10-minútový self-serve audit",
              "Reálne odhady AI citácií (6 modelov)",
              "Programmatic landing pages pre každú SK/CZ firmu",
              "26 jazykov · White-label pre agentúry",
              "14-dňová záruka vrátenia peňazí",
            ]}
          />
          <VerdictCard
            testid="verdict-ss"
            variant="neutral"
            brand="SPACE Solutions"
            price="€790 / audit"
            best="Pre korporátnych klientov ktorí chcú plnú konzultáciu + CMO prezentáciu"
            points={[
              "Human expert review (nie čisto AI)",
              "Osobná prezentácia výsledkov",
              "Dĺžka PDF reportu typicky 40-60 strán",
              "Pokrýva aj content strategy a brand positioning",
              "Dodávka do 48 h cez email",
            ]}
          />
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section id="comparison-table" className="px-6 py-12 max-w-4xl mx-auto">
        <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] mb-6" data-testid="compare-h2-table">
          Porovnanie 15 funkcií
        </h2>
        <div className="overflow-x-auto rounded-xl border border-white/10 bg-white/5">
          <table className="w-full text-sm" data-testid="compare-table">
            <thead className="bg-black/40">
              <tr>
                <th className="text-left px-4 py-3 font-bold text-gray-300">Funkcia</th>
                <th className="text-left px-4 py-3 font-bold text-[#a855f7]">TopRankPulse</th>
                <th className="text-left px-4 py-3 font-bold text-gray-300">SPACE Solutions</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, i) => (
                <tr key={row.feature} className={i % 2 ? "bg-white/[0.02]" : ""} data-testid={`compare-row-${i}`}>
                  <td className="px-4 py-3 text-gray-200">{row.feature}</td>
                  <td className={`px-4 py-3 font-semibold ${row.winner === "tp" ? "text-emerald-400" : "text-gray-200"}`}>
                    <Cell value={row.tp} highlight={row.winner === "tp"} />
                  </td>
                  <td className={`px-4 py-3 ${row.winner === "ss" ? "text-emerald-400 font-semibold" : "text-gray-400"}`}>
                    <Cell value={row.ss} highlight={row.winner === "ss"} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-black/40 border-t border-white/10">
              <tr>
                <td className="px-4 py-3 font-bold text-gray-200">Celkové skóre</td>
                <td className="px-4 py-3 font-black text-emerald-400">11 výhier · 4 remízy</td>
                <td className="px-4 py-3 text-gray-400">1 výhra · 4 remízy · 10 strát</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <p className="text-xs text-gray-500 mt-3" data-testid="compare-disclaimer">
          Zdroj cien: verejne dostupné cenníky SPACE Solutions k 02/2026. Porovnanie prebieha v súlade s EU
          Directive 2006/114/EC o klamlivej a porovnávacej reklame — všetky tvrdenia sú overiteľné.
        </p>
      </section>

      {/* CASE STUDY CROSS-LINK — pulls latest matching jendral article */}
      <CaseStudyLink competitorKey="space-solutions" />

      {/* WHY SWITCH */}
      <section className="px-6 py-12 max-w-4xl mx-auto" data-testid="compare-why-switch">
        <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] mb-6">
          Prečo prejsť zo SPACE Solutions na TopRankPulse?
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ReasonCard
            icon={Euro}
            title="113× lacnejší štart"
            body="€790 u SPACE vs. €7 u nás za porovnateľný AI SEO snapshot. Rozdiel €783 sa dá reinvestovať do content produkcie."
          />
          <ReasonCard
            icon={Clock}
            title="288× rýchlejšie"
            body="10 minút self-serve vs. 48-hodinové čakanie na manuálne vypracovaný PDF. Tvoj team môže reagovať ešte dnes."
          />
          <ReasonCard
            icon={Zap}
            title="Skutočné AI citation metriky"
            body="Meriame odhady citácií v 6 AI modeloch (ChatGPT, Perplexity, Gemini, Claude, Copilot, Mistral). SPACE kontroluje len schema + llms.txt."
          />
          <ReasonCard
            icon={Globe}
            title="Programmatic coverage"
            body="153 SK+CZ miest + 1000+ firiem s dedikovanými landing stránkami generovanými podľa IČO (ORSF.sk + ARES.gov.cz)."
          />
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 py-12 max-w-4xl mx-auto" data-testid="compare-faq">
        <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] mb-6">Časté otázky</h2>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <div key={f.q} className="rounded-xl border border-white/10 bg-white/5 overflow-hidden" data-testid={`compare-faq-${i}`}>
              <button
                type="button"
                onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-white/5"
                data-testid={`compare-faq-toggle-${i}`}
              >
                <span className="font-bold text-base">{f.q}</span>
                <span className={`text-gray-400 transition-transform ${faqOpen === i ? "rotate-45" : ""}`}>+</span>
              </button>
              {faqOpen === i && (
                <div className="px-5 pb-5 text-sm text-gray-300 leading-relaxed" data-testid={`compare-faq-answer-${i}`}>
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 py-16 max-w-4xl mx-auto" data-testid="compare-final-cta">
        <div className="rounded-2xl border border-[#a855f7]/40 bg-gradient-to-br from-[#a855f7]/20 via-[#a855f7]/5 to-transparent p-8 sm:p-10">
          <div className="flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-[#FFE600] mb-3 font-['JetBrains_Mono']">
            <ShieldCheck className="w-4 h-4" />
            <span>14-dňová záruka vrátenia peňazí</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-black font-['Cabinet_Grotesk'] mb-3 leading-tight">
            Nemusíš platiť €790, aby si zistil, kde ťa AI nevidí.
          </h2>
          <p className="text-gray-300 mb-6 max-w-xl">
            Spusti AI SEO Snapshot za <strong className="text-white">€7</strong>, dostaneš report
            za 10 minút. Ak sa ti nepáči, vrátime peniaze bez otázok.
          </p>
          <Link
            to="/snapshot"
            onClick={() => trackEvent("compare_final_cta_click", { destination: "/snapshot" })}
            className="inline-flex items-center gap-2 bg-[#a855f7] hover:bg-[#9333ea] text-white font-black px-7 py-4 rounded-lg text-lg transition-all"
            data-testid="compare-final-cta-btn"
          >
            Spustiť audit za €7 <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-[11px] text-gray-500 mt-4" data-testid="compare-final-cta-trust">
            🇸🇰 Prevádzkuje RESTOB s.r.o., Bratislava · GDPR-compliant · dáta neopúšťajú EU
          </p>
        </div>
      </section>
    </div>
  );
}

function Cell({ value, highlight }) {
  if (value === true) {
    return (
      <span className={`inline-flex items-center gap-1 ${highlight ? "text-emerald-400" : "text-emerald-500"}`}>
        <Check className="w-4 h-4" /> Áno
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex items-center gap-1 text-gray-500">
        <X className="w-4 h-4" /> Nie
      </span>
    );
  }
  return <span>{value}</span>;
}

function VerdictCard({ brand, price, best, points, variant, testid }) {
  const borderCls = variant === "win" ? "border-[#a855f7]/40" : "border-white/10";
  const bgCls = variant === "win" ? "bg-gradient-to-br from-[#a855f7]/15 to-transparent" : "bg-white/5";
  return (
    <div className={`rounded-xl border ${borderCls} ${bgCls} p-6`} data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-xl font-black font-['Cabinet_Grotesk']">{brand}</h3>
        {variant === "win" && (
          <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
            Lepšie pre väčšinu
          </span>
        )}
      </div>
      <p className="text-sm text-gray-300 mb-1">{price}</p>
      <p className="text-xs text-gray-400 mb-4 leading-relaxed">{best}</p>
      <ul className="space-y-2">
        {points.map((p) => (
          <li key={p} className="flex items-start gap-2 text-sm text-gray-200">
            <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${variant === "win" ? "text-[#a855f7]" : "text-gray-500"}`} />
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReasonCard({ icon: Icon, title, body }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-5">
      <Icon className="w-5 h-5 text-[#FFE600] mb-3" />
      <h3 className="font-bold text-base mb-1">{title}</h3>
      <p className="text-sm text-gray-400 leading-relaxed">{body}</p>
    </div>
  );
}
